import { computed, inject, Injectable, signal } from '@angular/core';
import { DayPreferences, Preferences } from '@app/preferences/models/preferences';
import { PreferencesService } from '@app/preferences/service/preferences-service';
import { WorkLocation } from '@app/shared/enum/WorkLocation';

import { getISODay } from 'date-fns';

import { TimeSheetTask } from '../models/time-sheet-task';
import { TimeSheetUpdate } from '../models/time-sheet-update';
import { createChangeKey } from '../util/time-sheet-utils';

@Injectable({
  providedIn: 'root',
})
export class TimeSheetChangeTrackerService {
  private preferencesService = inject(PreferencesService);
  private tracker = signal<Map<string, TimeSheetUpdate>>(new Map());

  readonly changes = computed(() => Array.from(this.tracker().values()));
  readonly pendingChanges = computed(() => this.changes().length !== 0);

  hasChanges(task: TimeSheetTask, date: Date): boolean {
    return this.tracker().has(createChangeKey(task, date));
  }

  getChange(task: TimeSheetTask, date: Date): TimeSheetUpdate | null {
    return this.tracker().get(createChangeKey(task, date)) ?? null;
  }

  private getDefaultsFromPreferences(date: Date): {
    startTime: string | null;
    lunchLength: string | null;
    workLocation: WorkLocation;
  } {
    const prefs = this.preferencesService.preferences();
    if (!prefs) {
      return { startTime: null, lunchLength: null, workLocation: WorkLocation.Remote };
    }

    const isoDay = getISODay(date);
    const dayPreferencesMap: Record<number, keyof Preferences> = {
      1: 'monday',
      2: 'tuesday',
      3: 'wednesday',
      4: 'thursday',
      5: 'friday',
    };

    const dayKey = dayPreferencesMap[isoDay];
    const dayPrefs = dayKey ? (prefs[dayKey] as DayPreferences) : null;

    return {
      startTime: prefs.start || null,
      lunchLength: prefs.lunch || null,
      workLocation: dayPrefs?.workLocation ?? WorkLocation.Remote,
    };
  }

  updateChange(
    task: TimeSheetTask,
    date: Date,
    updatedFields: Partial<TimeSheetUpdate>,
    serverEntry: TimeSheetUpdate | null
  ): void {
    const key = createChangeKey(task, date);

    this.tracker.update((current) => {
      const newTracker = new Map(current);
      const existing = newTracker.get(key);

      const defaults = this.getDefaultsFromPreferences(date);
      const base: TimeSheetUpdate = existing ?? {
        projectId: task.projectId,
        title: `${task.customerName} - ${task.taskName}`,
        customerName: task.customerName,
        taskName: task.taskName,
        dayOfMonth: date.getDate(),
        totalHours: serverEntry?.totalHours ?? null,
        startTime: serverEntry?.startTime ?? defaults.startTime,
        lunchLength: serverEntry?.lunchLength ?? defaults.lunchLength,
        description: serverEntry?.description ?? null,
        workLocation: serverEntry?.workLocation ?? defaults.workLocation,
      };

      if (updatedFields.totalHours && updatedFields.totalHours === '00:00') {
        updatedFields.totalHours = null;
      }

      newTracker.set(key, { ...base, ...updatedFields });
      return newTracker;
    });
  }

  setTracker(data: Map<string, TimeSheetUpdate>) {
    this.tracker.set(new Map(data));
  }

  removeChange(task: TimeSheetTask, date: Date): void {
    const key = createChangeKey(task, date);
    this.tracker.update((current) => {
      const newTracker = new Map(current);
      newTracker.delete(key);
      return newTracker;
    });
  }

  reset() {
    this.tracker.set(new Map());
  }
}
